import { boot } from "quasar/wrappers";

import { useCommonStore } from "src/stores/common-store";
import { usePersistedStore } from "src/stores/persisted-store";
import { useVersionUpdateStore } from "src/stores/versionupdate-store";
import { Platform } from "quasar";
import { checkForUpdatedVersion, decodeJwtToken } from "src/utils";
import { getAccountID } from "src/utils/commonFunction";
import { isUniversalLinkPath, setPendingDeepLink } from "src/utils/deepLink";
import firebase from "firebase/app";
import "firebase/auth";
import { identityAxiosInstance, setidentityAxiosHeaders } from "./axios";
import { useUserInfo } from "src/stores/user-store";
import { useNotificationDetails } from "src/stores/notification-store";
import { sendMessageToParentApp, signOutFirebaseUser } from "src/utils";
import LogRocket from "logrocket";

const userStore = useUserInfo();

// FTx Wallet only accepts access tokens that carry the "FTxWallet" scope.
// Tokens issued before the scope was added never gain it on refresh, so a user
// holding one is signed out once, at launch, to get a fresh token. The flag
// stops a sign-out loop if the identity server still does not grant the scope
// after they sign in again.
const FTX_WALLET_SCOPE = "FTxWallet";

function tokenHasWalletScope(claims) {
  const scopes = [].concat(claims?.scope || []).join(" ").split(" ");
  const audience = [].concat(claims?.aud || []);
  return (
    scopes.includes(FTX_WALLET_SCOPE) || audience.includes(FTX_WALLET_SCOPE)
  );
}

// Returns true when a sign-out was started.
function signOutIfTokenLacksWalletScope(router, persistedStore, commonStore) {
  if (String(process.env.ENABLE_FTX_WALLET) !== "true") return false;
  // embedded in the commerce app: the host owns the session
  if (commonStore.appInIframe) return false;

  const token = persistedStore?.user?.access_token;
  if (!token) return false;

  let claims = null;
  try {
    claims = decodeJwtToken(token);
  } catch (error) {
    return false;
  }

  const flagKey = `ftxWalletScopeSignOut-${claims?.sub || ""}`;
  try {
    if (tokenHasWalletScope(claims)) {
      localStorage.removeItem(flagKey);
      return false;
    }
    if (localStorage.getItem(flagKey)) {
      console.warn(
        "[FTx Wallet] token still has no FTxWallet scope after signing in again"
      );
      return false;
    }
    localStorage.setItem(flagKey, String(Date.now()));
  } catch (error) {
    // no storage means no loop guard: do not risk a sign-out loop
    return false;
  }

  // same steps as the Logout dialog
  userStore.logOutApi().finally(() => {
    persistedStore.logout(router);
    persistedStore.setTempUserInfo(null);
    persistedStore.setSignUpEmailPhoneExists("");
    persistedStore.setSignupAddOnParameters(null);
    signOutFirebaseUser();
  });
  return true;
}

export default boot(({ app, router, store }) => {
  router.beforeEach(async (to, from, next) => {
    //here need to code first
    const commonStore = useCommonStore(store);
    let persistedStore = usePersistedStore();
    let appLoaded = commonStore.appLoaded;
    // if (to.name != "Callback") {
    //   await persistedStore.mapUser()
    // }
    //remove search keyword from store so it becomes empty on other page

    if (commonStore.searchKeyword) commonStore.updateSearchkeyword("");
    commonStore.toggleHeaderSearch(false);

    if (!appLoaded) {
      commonStore.setLoader(true);
      await commonStore.mapInIframe();
      persistedStore.getToken(); //to set the token
      await persistedStore.mapUser();

      // Launch-time check: a session without the FTx Wallet scope is signed
      // out here, before the user starts using the app.
      if (
        to.name != "Callback" &&
        signOutIfTokenLacksWalletScope(router, persistedStore, commonStore)
      ) {
        return next(false);
      }
      ////~~~~Check User~~~~~/////
      await userStore.checkUserInLNS();
      await commonStore.getStoreConfiguration();
      if (persistedStore?.user && persistedStore?.user?.access_token) {
        await userStore.getUserDetails();
        await commonStore.getAlMyStores();
      }
      commonStore.setAppLoaded(true);
    }

    // Web/PWA push campaign open tracking. Runs before the login check so a
    // logged-out click is counted too (the API is open). Native tracks on tap
    // in App.vue, so skip it here. Not awaited — never delays navigation.
    if (to.name == "HandlePushNotificationPWA" && !Platform.is.cordova) {
      useNotificationDetails().trackPushCampaignOpen(to.query);
    }

    if (
      to.name != "NewLogin" &&
      to.name != "AgeVerification" &&
      !to.meta.showWithoutLogin &&
      (!persistedStore.user || !persistedStore.user.access_token) &&
      to.name != "Callback"
    ) {
      // Remember a deep link so login can resume it instead of dropping the
      // user on Home. Scoped to deep-link paths so the ordinary login flow is
      // untouched.
      if (isUniversalLinkPath(to.path)) setPendingDeepLink(to.fullPath);

      next({ name: "NewLogin" });
      // next({ name: "Login" });
    } else if (
      !userStore.userInLns &&
      to.name != "AccountLinked" &&
      // Self-contained public link, account linking can wait until after it
      // (white-label only, the marketplace keeps the old redirect)
      !(to.name == "VerifyPhone" && getAccountID())
    ) {
      // Account linking sits between login and the target. OidcCallback has
      // already consumed the first pending link, so re-arm it here or the
      // deep link is lost for users who still need to link.
      if (isUniversalLinkPath(to.path)) setPendingDeepLink(to.fullPath);

      next({ name: "AccountLinked" });
    } else {
      commonStore.showInstallAppBanner = false;
      if (commonStore.initialPage && commonStore.appInIframe) {
        next(commonStore.initialPage);
        commonStore.initialPage = null;
      } else next();
    }
    commonStore.setLoader(false);

    if (to?.fullPath && commonStore.appInIframe)
      sendMessageToParentApp({
        action: appLoaded ? "NAVIGATION" : "ROUTE_REPLACE",
        value: to.fullPath,
      });
  });

  router.afterEach(async (routeTo, routeFrom) => {
    const commonStore = useCommonStore(store);
    // Optional: Track page views with the router
    // LogRocket.log("Page view", routeTo.fullPath);
    if (Platform.is?.mobile) {
      commonStore.setHeader(
        routeTo.meta.header?.account && getAccountID()
          ? routeTo.meta.header.account
          : routeTo.meta.header
      );
    }

    // For Global search
    if (routeTo.name == "Home") {
      commonStore.resetGlobalSearchData();
      commonStore.toggleGlobalSearch({
        showGlobalSearch: false,
        searchText: "",
      });
    }

    //switch off the loader
    if (commonStore.showFullScreenLoader) commonStore.setLoader(false);

    //set route histories
    if (routeTo.name != routeFrom.name && routeFrom.name) {
      let backRoute = commonStore.getBackRoute;
      if (
        (commonStore.isBackEvent && routeTo.meta.clearHistory) ||
        commonStore.blnClearHistory
      ) {
        commonStore.clearAllHistory();
      } else if (backRoute && backRoute.name == routeTo.name) {
        commonStore.popFromHistory();
      } else if (!routeFrom.meta.disableHistory) {
        commonStore.addInHistory(routeFrom);
      }
    }
  });
});
